import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { ConversationPlatform } from "../conversation";
import {
  ConversationAssigneeSummarySchema,
  ConversationCategorySchema,
  ConversationResolutionSentimentSchema,
  SupportActionSchema,
} from "../conversation-insights";
import { ConversationTableSortSchema } from "../tables/conversations-table-sort";
import {
  ChannelFilterType,
  ConversationKnownDateFilterTimeFrame,
  CustomDateSchema,
  DateFilterQueryType,
  FiltersStatusSchema,
  PendingFilter,
} from "./conversation-filters";

export const CONVERSATIONS_INSIGHTS_FILTERS_VERSION = "1";

export enum FilterGroupInsightsFilterOption {
  CHANNELS = "channels",
  CREATED_DATE = "createdDate",
  CLOSED_DATE = "closedDate",
  LAST_RESPONSE_AT = "lastResponseAt",
  CONVERSATION_INSIGHTS_SUPPORT_ACTIONS = "conversationInsightsSupportActions",
  CONVERSATION_INSIGHTS_CATEGORIES = "conversationInsightsCategories",
  CONVERSATION_INSIGHTS_SENTIMENT = "conversationInsightsSentiment",
  CONVERSATION_INSIGHTS_ASSIGNEE_SUMMARY = "conversationInsightsAssigneeSummary",
  CONVERSATION_INSIGHTS_AI_CATEGORIES = "conversationInsightsAiCategories",
  CONVERSATION_INSIGHTS_TOOL_NAMES = "conversationInsightsToolNames",
  CONVERSATION_INSIGHTS_TOOL_METRICS = "conversationInsightsToolMetrics",
  CONVERSATION_INSIGHTS_TOOL_ERROR_TYPES = "conversationInsightsToolErrorTypes",
}

export enum ConversationInsightsSupportActionsFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsCategoriesFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsSentimentFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsAssigneeSummaryFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsAiCategoriesFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsToolNamesFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsToolMetricsFilterType {
  TOTAL_CALLS_GREATER_THAN = "totalCallsGreaterThan",
  TOTAL_CALLS_LESS_THAN = "totalCallsLessThan",
  TOTAL_CALLS_EQUAL_TO = "totalCallsEqualTo",
  SUCCESS_COUNT_GREATER_THAN = "successCountGreaterThan",
  SUCCESS_COUNT_LESS_THAN = "successCountLessThan",
  SUCCESS_COUNT_EQUAL_TO = "successCountEqualTo",
  ERROR_COUNT_GREATER_THAN = "errorCountGreaterThan",
  ERROR_COUNT_LESS_THAN = "errorCountLessThan",
  ERROR_COUNT_EQUAL_TO = "errorCountEqualTo",
}

export enum ConversationInsightsToolErrorTypesFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

// Zod schemas for validation
export const FilterGroupInsightsFilterOptionSchema = createEnum(
  FilterGroupInsightsFilterOption,
);
export const ConversationPlatformSchema = createEnum(ConversationPlatform);
export const ChannelFilterTypeSchema = createEnum(ChannelFilterType);
export const DateFilterQueryTypeSchema = createEnum(DateFilterQueryType);
export const KnownDateFilterTimeFrameSchema = createEnum(
  ConversationKnownDateFilterTimeFrame,
);
export const ConversationInsightsSupportActionsFilterTypeSchema = createEnum(
  ConversationInsightsSupportActionsFilterType,
);
export const ConversationInsightsCategoriesFilterTypeSchema = createEnum(
  ConversationInsightsCategoriesFilterType,
);
export const ConversationInsightsSentimentFilterTypeSchema = createEnum(
  ConversationInsightsSentimentFilterType,
);
export const ConversationInsightsAssigneeSummaryFilterTypeSchema = createEnum(
  ConversationInsightsAssigneeSummaryFilterType,
);
export const ConversationInsightsAiCategoriesFilterTypeSchema = createEnum(
  ConversationInsightsAiCategoriesFilterType,
);
export const ConversationInsightsToolNamesFilterTypeSchema = createEnum(
  ConversationInsightsToolNamesFilterType,
);
export const ConversationInsightsToolMetricsFilterTypeSchema = createEnum(
  ConversationInsightsToolMetricsFilterType,
);
export const ConversationInsightsToolErrorTypesFilterTypeSchema = createEnum(
  ConversationInsightsToolErrorTypesFilterType,
);

// Individual filter schemas
export const ChannelsInsightsFilterSchema = z.object({
  type: z.literal(FilterGroupInsightsFilterOption.CHANNELS),
  value: z.array(ConversationPlatformSchema),
  query: ChannelFilterTypeSchema,
});

export const CreatedDateInsightsFilterSchema = z.object({
  type: z.literal(FilterGroupInsightsFilterOption.CREATED_DATE),
  value: KnownDateFilterTimeFrameSchema,
  query: DateFilterQueryTypeSchema,
  customDate: CustomDateSchema.optional(),
});

export const LastResponseAtInsightsFilterSchema = z.object({
  type: z.literal(FilterGroupInsightsFilterOption.LAST_RESPONSE_AT),
  value: KnownDateFilterTimeFrameSchema,
  query: DateFilterQueryTypeSchema,
  customDate: CustomDateSchema.optional(),
});

export const ClosedDateInsightsFilterSchema = z.object({
  type: z.literal(FilterGroupInsightsFilterOption.CLOSED_DATE),
  value: KnownDateFilterTimeFrameSchema,
  query: DateFilterQueryTypeSchema,
  customDate: CustomDateSchema.optional(),
});

export const ConversationInsightsSupportActionsFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_SUPPORT_ACTIONS,
  ),
  value: z.array(SupportActionSchema),
  query: ConversationInsightsSupportActionsFilterTypeSchema,
});

export const ConversationInsightsCategoriesFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_CATEGORIES,
  ),
  value: z.array(ConversationCategorySchema),
  query: ConversationInsightsCategoriesFilterTypeSchema,
});

export const ConversationInsightsSentimentFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_SENTIMENT,
  ),
  value: z.array(ConversationResolutionSentimentSchema),
  query: ConversationInsightsSentimentFilterTypeSchema,
});

export const ConversationInsightsAssigneeSummaryFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_ASSIGNEE_SUMMARY,
  ),
  value: z.array(ConversationAssigneeSummarySchema),
  query: ConversationInsightsAssigneeSummaryFilterTypeSchema,
});

export const ConversationInsightsAiCategoriesFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_AI_CATEGORIES,
  ),
  value: z.array(z.string()),
  query: ConversationInsightsAiCategoriesFilterTypeSchema,
});

export const ConversationInsightsToolNamesFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_TOOL_NAMES,
  ),
  value: z.array(z.string()),
  query: ConversationInsightsToolNamesFilterTypeSchema,
});

export const ConversationInsightsToolMetricsFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_TOOL_METRICS,
  ),
  value: z.number(), // Only numeric values for metrics
  query: ConversationInsightsToolMetricsFilterTypeSchema,
});

export const ConversationInsightsToolErrorTypesFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_TOOL_ERROR_TYPES,
  ),
  value: z.array(z.string()), // Array of error type strings
  query: ConversationInsightsToolErrorTypesFilterTypeSchema,
});

export const AdvancedInsightsFilterSchema = z.discriminatedUnion("type", [
  ChannelsInsightsFilterSchema,
  CreatedDateInsightsFilterSchema,
  LastResponseAtInsightsFilterSchema,
  ClosedDateInsightsFilterSchema,
  ConversationInsightsSupportActionsFilterSchema,
  ConversationInsightsCategoriesFilterSchema,
  ConversationInsightsSentimentFilterSchema,
  ConversationInsightsAssigneeSummaryFilterSchema,
  ConversationInsightsAiCategoriesFilterSchema,
  ConversationInsightsToolNamesFilterSchema,
  ConversationInsightsToolMetricsFilterSchema,
  ConversationInsightsToolErrorTypesFilterSchema,
]);

export const UniqueInsightsV1FiltersSchema = z.object({
  status: FiltersStatusSchema.optional().nullable(),
  sort: ConversationTableSortSchema.optional(),
});

export const ConversationInsightsFiltersV1Schema =
  UniqueInsightsV1FiltersSchema.extend({
    advancedFilters: z.array(AdvancedInsightsFilterSchema),
  });

// Type exports generated from schemas (complex types only - enums are already exported above)
export type ChannelsInsightsFilter = z.infer<
  typeof ChannelsInsightsFilterSchema
>;
export type CreatedDateInsightsFilter = z.infer<
  typeof CreatedDateInsightsFilterSchema
>;
export type LastResponseAtInsightsFilter = z.infer<
  typeof LastResponseAtInsightsFilterSchema
>;
export type ClosedDateInsightsFilter = z.infer<
  typeof ClosedDateInsightsFilterSchema
>;
export type ConversationInsightsSupportActionsFilter = z.infer<
  typeof ConversationInsightsSupportActionsFilterSchema
>;
export type ConversationInsightsCategoriesFilter = z.infer<
  typeof ConversationInsightsCategoriesFilterSchema
>;
export type ConversationInsightsSentimentFilter = z.infer<
  typeof ConversationInsightsSentimentFilterSchema
>;
export type ConversationInsightsAssigneeSummaryFilter = z.infer<
  typeof ConversationInsightsAssigneeSummaryFilterSchema
>;
export type ConversationInsightsAiCategoriesFilter = z.infer<
  typeof ConversationInsightsAiCategoriesFilterSchema
>;
export type ConversationInsightsToolNamesFilter = z.infer<
  typeof ConversationInsightsToolNamesFilterSchema
>;
export type ConversationInsightsToolMetricsFilter = z.infer<
  typeof ConversationInsightsToolMetricsFilterSchema
>;
export type ConversationInsightsToolErrorTypesFilter = z.infer<
  typeof ConversationInsightsToolErrorTypesFilterSchema
>;

export type AdvancedInsightsFilter = z.infer<
  typeof AdvancedInsightsFilterSchema
>;
export type UniqueInsightsV1Filters = z.infer<
  typeof UniqueInsightsV1FiltersSchema
>;
export type ConversationInsightsFiltersV1 = z.infer<
  typeof ConversationInsightsFiltersV1Schema
>;

export type PendingChannelsInsightsFilter =
  PendingFilter<ChannelsInsightsFilter>;
export type PendingCreatedDateInsightsFilter =
  PendingFilter<CreatedDateInsightsFilter>;
export type PendingLastResponseAtInsightsFilter =
  PendingFilter<LastResponseAtInsightsFilter>;
export type PendingClosedDateInsightsFilter =
  PendingFilter<ClosedDateInsightsFilter>;
export type PendingConversationInsightsSupportActionsFilter =
  PendingFilter<ConversationInsightsSupportActionsFilter>;
export type PendingConversationInsightsCategoriesFilter =
  PendingFilter<ConversationInsightsCategoriesFilter>;
export type PendingConversationInsightsSentimentFilter =
  PendingFilter<ConversationInsightsSentimentFilter>;
export type PendingConversationInsightsAssigneeSummaryFilter =
  PendingFilter<ConversationInsightsAssigneeSummaryFilter>;
export type PendingConversationInsightsAiCategoriesFilter =
  PendingFilter<ConversationInsightsAiCategoriesFilter>;
export type PendingConversationInsightsToolNamesFilter =
  PendingFilter<ConversationInsightsToolNamesFilter>;
export type PendingConversationInsightsToolMetricsFilter =
  PendingFilter<ConversationInsightsToolMetricsFilter>;
export type PendingConversationInsightsToolErrorTypesFilter =
  PendingFilter<ConversationInsightsToolErrorTypesFilter>;

export type PendingAdvancedInsightsFilter =
  | PendingChannelsInsightsFilter
  | PendingCreatedDateInsightsFilter
  | PendingLastResponseAtInsightsFilter
  | PendingClosedDateInsightsFilter
  | PendingConversationInsightsSupportActionsFilter
  | PendingConversationInsightsCategoriesFilter
  | PendingConversationInsightsSentimentFilter
  | PendingConversationInsightsAssigneeSummaryFilter
  | PendingConversationInsightsAiCategoriesFilter
  | PendingConversationInsightsToolNamesFilter
  | PendingConversationInsightsToolMetricsFilter
  | PendingConversationInsightsToolErrorTypesFilter;
